import { API_BASE_URL } from 'envConfig';
import { LocalStorageAuth } from 'utils/LocalStorageAuth';

/**
 * Small WHEP player. MediaMTX accepts a recvonly SDP offer at `whepUrl` and returns the answer.
 * The read token is a query parameter; callers must not log the URL after the token is attached.
 * An https page cannot call an http IP directly, so that offer is posted through this API instead.
 */

const iceComplete = (pc: RTCPeerConnection, signal: AbortSignal) =>
    new Promise<void>((resolve, reject) => {
        if (pc.iceGatheringState === 'complete') {
            resolve();
            return;
        }
        const onChange = () => {
            if (pc.iceGatheringState === 'complete') {
                cleanup();
                resolve();
            }
        };
        const onAbort = () => {
            cleanup();
            reject(new DOMException('aborted', 'AbortError'));
        };
        const cleanup = () => {
            pc.removeEventListener('icegatheringstatechange', onChange);
            signal.removeEventListener('abort', onAbort);
        };
        signal.addEventListener('abort', onAbort);
        pc.addEventListener('icegatheringstatechange', onChange);
    });

export interface WhepConnection {
    close: () => void;
    closed: Promise<void>;
}

/** POST the offer and play the answer in `video`. Resolves once the answer is applied. */
export const connectWhep = async (
    whepUrl: string,
    token: string,
    video: HTMLVideoElement,
    signal: AbortSignal,
): Promise<WhepConnection> => {
    if (signal.aborted) throw new DOMException('aborted', 'AbortError');
    const pc = new RTCPeerConnection();
    let closed = false;
    const closedPromise = new Promise<void>((resolve) => {
        pc.addEventListener('connectionstatechange', () => {
            if (pc.connectionState === 'failed' || pc.connectionState === 'closed') {
                closed = true;
                resolve();
            }
        });
    });
    pc.addTransceiver('video', { direction: 'recvonly' });
    pc.ontrack = (event) => {
        const [stream] = event.streams;
        if (stream) video.srcObject = stream;
        else {
            const mixed = new MediaStream();
            mixed.addTrack(event.track);
            video.srcObject = mixed;
        }
        void video.play().catch(() => undefined);
    };
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    await Promise.race([
        iceComplete(pc, signal),
        new Promise((resolve) => {
            const timer = setTimeout(resolve, 2000);
            signal.addEventListener('abort', () => clearTimeout(timer), { once: true });
        }),
    ]);
    const sdp = pc.localDescription?.sdp || '';
    const screenId = whepUrl.match(/\/screens\/([0-9a-f]{24})\/whep/i)?.[1] || '';
    const proxy = whepUrl.startsWith('http://') && window.location.protocol === 'https:' && screenId;
    const headers: Record<string, string> = { 'Content-Type': 'application/sdp' };
    let resource = `${whepUrl}${whepUrl.includes('?') ? '&' : '?'}token=${encodeURIComponent(token)}`;
    if (proxy) {
        const base = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
        resource = `${base}/bos/screens/${screenId}/live/whep`;
        const auth = LocalStorageAuth.getAuthHeaders();
        if (auth.Authorization) headers.Authorization = auth.Authorization;
        const companyId = LocalStorageAuth.getActiveCompanyId();
        if (companyId) headers['X-Active-Company-Id'] = companyId;
    }
    const response = await fetch(resource, {
        method: 'POST',
        headers,
        body: sdp,
        signal,
    });
    if (!response.ok) {
        pc.close();
        throw new Error(`live view signalling failed (${response.status})`);
    }
    const answer = await response.text();
    await pc.setRemoteDescription({ type: 'answer', sdp: answer });
    const close = () => {
        if (closed) return;
        closed = true;
        pc.close();
        video.srcObject = null;
    };
    signal.addEventListener('abort', close, { once: true });
    return { close, closed: closedPromise };
};
